// client/src/pages/MySitePage.js
// My Site workflow page - contains site/domain-level analysis tools
// UPDATED Apr 13 2026: Removed KG Completeness, Authority, and Future Features tabs

import React, { useState } from 'react';
import SidebarLayout from '../components/SidebarLayout';

// Import existing components
import KnowledgeGraphDashboard from '../modules/KnowledgeGraphDashboard2';
import DomainChecksDashboard from '../components/DomainChecksDashboard';

// Load Work Sans font
if (typeof document !== 'undefined') {
  const fontLink = document.createElement('link');
  fontLink.href = 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700;800&display=swap';
  fontLink.rel = 'stylesheet';
  if (!document.querySelector('link[href*="Work+Sans"]')) {
    document.head.appendChild(fontLink);
  }
}

// Brand colors
const colors = {
  primary: '#39393a',      // Jet Black
  secondary: '#048ba8',    // Blue Munsell
  tertiary: '#e9c46a',     // Saffron
  accent: '#f4a261',       // Sandy Brown
  grey: '#e76f51',         // Burnt Sienna
  green: '#01796f'         // Pine Green
};

const MySitePage = ({ campaign, campaigns, apiBaseUrl }) => {
  const [activeTab, setActiveTab] = useState('kg-overview');

  const tabs = [
    { 
      id: 'kg-overview', 
      label: 'Knowledge Graph',
      group: 'knowledge-graph',
      description: 'Entity presence and KGIS score'
    },
    { 
      id: 'domain-checks', 
      label: 'Domain Checks',
      group: 'domain',
      description: 'llms.txt, robots.txt, and site configuration'
    }
  ];

  const groupedTabs = {
    'knowledge-graph': tabs.filter(t => t.group === 'knowledge-graph'),
    'domain': tabs.filter(t => t.group === 'domain')
  };

  return (
    <SidebarLayout currentCampaign={campaign} campaigns={campaigns} apiBaseUrl={apiBaseUrl}>
      <div style={{ 
        padding: '24px',
        fontFamily: "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      }}>
        {/* Top-Level Navigation */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          marginBottom: '24px'
        }}>
          {/* Main Section Tabs - UPPERCASE */}
          <div style={{ borderBottom: '1px solid #e5e7eb' }}>
            <nav style={{ display: 'flex', gap: '32px', padding: '0 24px' }}>
              <button
                onClick={() => {
                  if (!groupedTabs['knowledge-graph'].some(t => t.id === activeTab)) {
                    setActiveTab('kg-overview');
                  }
                }}
                style={{
                  padding: '16px 0',
                  fontSize: '13px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  transition: 'all 0.2s',
                  borderBottom: groupedTabs['knowledge-graph'].some(t => t.id === activeTab) ? `2px solid ${colors.primary}` : '2px solid transparent',
                  marginBottom: '-1px',
                  color: groupedTabs['knowledge-graph'].some(t => t.id === activeTab) ? '#111827' : '#6b7280',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!groupedTabs['knowledge-graph'].some(t => t.id === activeTab)) {
                    e.currentTarget.style.color = '#374151';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!groupedTabs['knowledge-graph'].some(t => t.id === activeTab)) {
                    e.currentTarget.style.color = '#6b7280';
                  }
                }}
              >
                Knowledge Graph
              </button>
              <button
                onClick={() => {
                  if (!groupedTabs['domain'].some(t => t.id === activeTab)) {
                    setActiveTab('domain-checks');
                  }
                }}
                style={{
                  padding: '16px 0',
                  fontSize: '13px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  transition: 'all 0.2s',
                  borderBottom: groupedTabs['domain'].some(t => t.id === activeTab) ? `2px solid ${colors.primary}` : '2px solid transparent',
                  marginBottom: '-1px',
                  color: groupedTabs['domain'].some(t => t.id === activeTab) ? '#111827' : '#6b7280',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!groupedTabs['domain'].some(t => t.id === activeTab)) {
                    e.currentTarget.style.color = '#374151';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!groupedTabs['domain'].some(t => t.id === activeTab)) {
                    e.currentTarget.style.color = '#6b7280';
                  }
                }}
              >
                Domain Checks
              </button>
            </nav>
          </div>

          {/* Sub-Navigation */}
          <div style={{ 
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb'
          }}>
            <nav style={{ display: 'flex', flexWrap: 'wrap', padding: '0 24px' }}>
              {groupedTabs['knowledge-graph'].some(t => t.id === activeTab) && 
                groupedTabs['knowledge-graph'].map((tab) => (
                  <TabButton 
                    key={tab.id}
                    tab={tab}
                    isActive={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  />
                ))
              }
              {groupedTabs['domain'].some(t => t.id === activeTab) && 
                groupedTabs['domain'].map((tab) => (
                  <TabButton 
                    key={tab.id}
                    tab={tab}
                    isActive={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  />
                ))
              }
            </nav>
          </div>

          {/* Help Text */}
          <div style={{ 
            padding: '12px 24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            backgroundColor: `${colors.tertiary}20`
          }}>
            <div style={{
              flexShrink: 0,
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: '700',
              backgroundColor: colors.tertiary,
              color: colors.primary
            }}>
              i
            </div>
            <p style={{ 
              fontSize: '14px',
              color: colors.primary,
              margin: 0,
              lineHeight: '1.5'
            }}>
              <span style={{ fontWeight: '600' }}>My Site</span> analyzes domain-level factors that affect your overall authority and trust in LLM systems. 
              These factors apply across all your URLs.
            </p>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'kg-overview' && campaign && (
            <KnowledgeGraphDashboard
              campaignId={campaign.id}
              userId={campaign.userId}
              campaignName={campaign.name}
              urls={campaign.urls}
              apiBaseUrl={apiBaseUrl}
              activeTab="overview"
            />
          )}

          {activeTab === 'domain-checks' && campaign && (
            <DomainChecksDashboard 
              campaignId={campaign.id}
              urls={campaign.urls}
              apiBaseUrl={apiBaseUrl}
            />
          )}

          {!campaign && (
            <div style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: '8px',
              padding: '16px'
            }}>
              <p style={{ color: '#78350f', margin: 0 }}>
                Please select a campaign from the sidebar to view site-level audits
              </p>
            </div>
          )}
        </div>
      </div>
    </SidebarLayout>
  );
};

// Reusable Tab Button Component - Saffron/Gold brand color
const TabButton = ({ tab, isActive, onClick }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: isActive ? '600' : '500',
        transition: 'all 0.2s',
        borderBottom: isActive ? `2px solid ${colors.tertiary}` : isHovered ? '2px solid #d1d5db' : '2px solid transparent',
        color: isActive ? '#111827' : isHovered ? '#111827' : '#6b7280',
        background: 'none',
        border: 'none',
        cursor: 'pointer'
      }}
    >
      <span>{tab.label}</span>
    </button>
  );
};

export default MySitePage;
